import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { AlertTriangle, Phone, MapPin, Loader2, LocateFixed } from 'lucide-react';

type GeoPermission = 'checking' | 'prompt' | 'granted' | 'denied' | 'unsupported';
type LookupStatus = 'idle' | 'locating' | 'searching' | 'done' | 'error';

type NearestVet = {
  name: string;
  address?: string;
  phone?: string;
  mapsUrl?: string;
  distanceKm?: number;
  lat?: number;
  lng?: number;
};

// KodaCare warm-neutral map style
const KODA_MAP_STYLES: any[] = [
  { elementType: 'geometry', stylers: [{ color: '#f5efe6' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5b4848' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#fdf8f2' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#e8d8c4' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#d4b896' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a6a4a' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c5d8e8' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#6b8fa8' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eddfc9' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#d6e8c8' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#8a6a4a' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#e2d0b8' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#c4a882' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#ae9070' }] },
];

function KodaMap({ vetName, vetLat, vetLng, userLat, userLng, mapsUrl }: {
  vetName: string;
  vetLat: number;
  vetLng: number;
  userLat: number;
  userLng: number;
  mapsUrl?: string;
}) {
  const mapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const win = window as any;
    if (!mapRef.current || !win.google?.maps) return;

    const map = new win.google.maps.Map(mapRef.current, {
      center: { lat: vetLat, lng: vetLng },
      zoom: 14,
      styles: KODA_MAP_STYLES,
      disableDefaultUI: true,
      zoomControl: true,
      gestureHandling: 'cooperative',
    });

    // Vet marker — primary-brown pin
    new win.google.maps.Marker({
      position: { lat: vetLat, lng: vetLng },
      map,
      title: vetName,
      icon: {
        path: win.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#C17A2E',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2.5,
      },
    });

    // User marker — dark dot
    new win.google.maps.Marker({
      position: { lat: userLat, lng: userLng },
      map,
      title: 'Your location',
      icon: {
        path: win.google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: '#5b4848',
        fillOpacity: 0.85,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      },
    });

    // Dashed line between user and vet
    new win.google.maps.Polyline({
      path: [
        { lat: userLat, lng: userLng },
        { lat: vetLat, lng: vetLng },
      ],
      map,
      strokeColor: '#C17A2E',
      strokeOpacity: 0,
      icons: [{
        icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.7, strokeColor: '#C17A2E', scale: 3 },
        offset: '0',
        repeat: '16px',
      }],
    });
  }, [vetLat, vetLng, userLat, userLng, vetName]);

  return (
    <div className="rounded-2xl overflow-hidden border border-primary-brown/20 shadow-sm">
      <div className="flex items-center gap-2 px-4 py-3 bg-primary-brown/10 border-b border-primary-brown/15">
        <MapPin size={13} className="text-primary-brown shrink-0" />
        <span className="text-xs font-semibold text-primary-brown truncate flex-1">{vetName}</span>
        {mapsUrl && (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-primary-brown hover:underline shrink-0"
          >
            Open in Maps ↗
          </a>
        )}
      </div>
      <div ref={mapRef} className="w-full" style={{ height: 220 }} />
    </div>
  );
}

let mapsBootstrapPromise: Promise<void> | null = null;

function loadGoogleMapsBootstrap(): Promise<void> {
  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  if (!key) {
    return Promise.reject(new Error('Missing VITE_GOOGLE_MAPS_API_KEY'));
  }

  const win = window as any;
  // Already bootstrapped
  if (win.google?.maps?.importLibrary) return Promise.resolve();
  if (mapsBootstrapPromise) return mapsBootstrapPromise;

  // Poll for importLibrary to be ready — works whether the script is freshly
  // injected or was already present (onload fires before importLibrary exists
  // with the async bootstrap loader).
  function pollForImportLibrary(resolve: () => void, reject: (e: Error) => void) {
    const deadline = Date.now() + 15000;
    const poll = setInterval(() => {
      if ((window as any).google?.maps?.importLibrary) {
        clearInterval(poll);
        resolve();
      } else if (Date.now() > deadline) {
        clearInterval(poll);
        reject(new Error('Google Maps bootstrap timed out'));
      }
    }, 50);
  }

  mapsBootstrapPromise = new Promise<void>((resolve, reject) => {
    if (document.getElementById('google-maps-bootstrap')) {
      // Script tag already injected — just wait for importLibrary to appear.
      pollForImportLibrary(resolve, reject);
      return;
    }

    // Inject the async bootstrap script (recommended loading pattern)
    const script = document.createElement('script');
    script.id = 'google-maps-bootstrap';
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&v=weekly&loading=async&libraries=places`;
    // Don't resolve on onload — importLibrary may not exist yet at that point.
    script.onload = () => pollForImportLibrary(resolve, reject);
    script.onerror = () => {
        mapsBootstrapPromise = null; // allow retry
        reject(new Error('Failed to load Google Maps'));
      };
    document.head.appendChild(script);
  });

  return mapsBootstrapPromise;
}

function getCurrentPosition(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        });
      },
      (err) => reject(err),
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 30000,
      }
    );
  });
}

function toLatLng(loc: any): { lat: number; lng: number } | null {
  if (!loc) return null;
  const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
  const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  return { lat, lng };
}

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (v: number) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s1 = Math.sin(dLat / 2) ** 2;
  const s2 = Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(s1 + s2), Math.sqrt(1 - s1 - s2));
  return R * c;
}

async function findNearestVetNew(origin: { lat: number; lng: number }): Promise<NearestVet | null> {
  const { Place, SearchNearbyRankPreference } = await (window as any).google.maps.importLibrary('places');

  if (!Place?.searchNearby) throw new Error('Places searchNearby not available');

  const radii = [2000, 10000, 50000, 200000, 1000000];

  for (const radius of radii) {
    const response = await Place.searchNearby({
      fields: ['displayName', 'formattedAddress', 'internationalPhoneNumber', 'googleMapsURI', 'location'],
      includedTypes: ['veterinary_care'],
      maxResultCount: 5,
      rankPreference: SearchNearbyRankPreference?.DISTANCE,
      locationRestriction: { center: origin, radius },
    });

    const places = response?.places ?? [];
    if (!places.length) continue;

    const p = places[0];
    const placePos = toLatLng(p.location);
    const distanceKm = placePos ? haversineKm(origin, placePos) : undefined;

    return {
      name: p.displayName?.text || p.name || 'Nearest veterinary clinic',
      address: p.formattedAddress,
      phone: p.internationalPhoneNumber,
      mapsUrl: p.googleMapsURI || p.googleMapsUri,
      distanceKm,
      lat: placePos?.lat,
      lng: placePos?.lng,
    };
  }
  return null;
}

function findNearestVetLegacy(origin: { lat: number; lng: number }): Promise<NearestVet | null> {
  return new Promise((resolve, reject) => {
    const win = window as any;
    const google = win.google;
    if (!google?.maps?.places?.PlacesService) {
      reject(new Error('Legacy PlacesService not available'));
      return;
    }
    // PlacesService requires a map or a div
    const dummy = document.createElement('div');
    const service = new google.maps.places.PlacesService(dummy);
    const radii = [2000, 10000, 50000, 200000];
    let idx = 0;

    const tryNext = () => {
      if (idx >= radii.length) { resolve(null); return; }
      const radius = radii[idx++];
      service.nearbySearch(
        {
          location: new google.maps.LatLng(origin.lat, origin.lng),
          radius,
          type: 'veterinary_care',
        },
        (results: any[], status: string) => {
          if (status !== 'OK' || !results?.length) { tryNext(); return; }
          const p = results[0];
          const placePos = toLatLng(p.geometry?.location);
          const distanceKm = placePos ? haversineKm(origin, placePos) : undefined;
          resolve({
            name: p.name || 'Nearest veterinary clinic',
            address: p.vicinity,
            mapsUrl: `https://www.google.com/maps/place/?q=place_id:${p.place_id}`,
            distanceKm,
            lat: placePos?.lat,
            lng: placePos?.lng,
          });
        }
      );
    };
    tryNext();
  });
}

async function findNearestVet(origin: { lat: number; lng: number }): Promise<NearestVet | null> {
  await loadGoogleMapsBootstrap();

  // Try new Places API first, fall back to legacy nearbySearch
  try {
    const result = await findNearestVetNew(origin);
    if (result) return result;
  } catch (err) {
    console.warn('[Emergency] New Places API failed, trying legacy:', err);
  }

  return findNearestVetLegacy(origin);
}

export function Emergency() {
  const [permission, setPermission] = useState<GeoPermission>('checking');
  const [status, setStatus] = useState<LookupStatus>('idle');
  const [errorText, setErrorText] = useState<string>('');
  const [nearest, setNearest] = useState<NearestVet | null>(null);
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const findHelpNow = useCallback(async () => {
    try {
      setErrorText('');
      setStatus('locating');

      // Hard timeout — if still searching after 20 s, bail out with a useful message
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        setStatus('error');
        setErrorText('Search timed out. Use the button below to find an emergency vet on Google Maps.');
        const c = userCoords;
        setNearest({
          name: 'Search on Google Maps',
          mapsUrl: c
            ? `https://www.google.com/maps/search/emergency+vet/@${c.lat},${c.lng},13z`
            : `https://www.google.com/maps/search/emergency+vet+near+me`,
        });
      }, 20000);

      // Pre-kick Maps bootstrap in parallel with waiting for location —
      // so it's ready (or nearly ready) by the time coords arrive.
      void loadGoogleMapsBootstrap().catch(() => {});

      const coords = await getCurrentPosition();
      setPermission('granted');

      setUserCoords(coords);
      setStatus('searching');
      const result = await findNearestVet(coords);
      clearTimeout(searchTimeoutRef.current!);
      if (!result) {
        setStatus('error');
        setErrorText('No nearby veterinary clinic found yet. Please try again.');
        return;
      }

      setNearest(result);
      setStatus('done');
    } catch (err: any) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      console.error('[Emergency] findHelpNow error:', err?.message ?? err);
      if (err?.code === 1) {
        setPermission('denied');
        setErrorText('Location permission denied. Enable location to find nearest emergency vet help.');
        setStatus('error');
      } else {
        // Always give the user something useful — a Maps search pre-seeded with
        // their coordinates if we have them, otherwise a generic search.
        const coords = userCoords;
        const mapsUrl = coords
          ? `https://www.google.com/maps/search/emergency+vet/@${coords.lat},${coords.lng},13z`
          : `https://www.google.com/maps/search/emergency+vet+near+me`;
        setNearest({ name: 'Search on Google Maps', mapsUrl });
        setErrorText(
          String(err?.message || '').includes('VITE_GOOGLE_MAPS_API_KEY') || String(err?.message || '').includes('Missing VITE_GOOGLE_MAPS_API_KEY')
            ? 'Map search unavailable. Use the button below to find an emergency vet on Google Maps.'
            : `Could not load map results. Use the button below to search on Google Maps.`
        );
        setStatus('done');
      }
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const checkPermission = async () => {
      if (!navigator.geolocation) {
        if (mounted) {
          setPermission('unsupported');
          setStatus('error');
          setErrorText('Geolocation is not supported on this device/browser.');
        }
        return;
      }

      try {
        const perms = (navigator as any).permissions;
        if (!perms?.query) {
          if (mounted) setPermission('prompt');
          return;
        }

        const result = await perms.query({ name: 'geolocation' });
        if (!mounted) return;

        if (result.state === 'granted') {
          setPermission('granted');
          void findHelpNow();
        } else if (result.state === 'denied') {
          setPermission('denied');
        } else {
          // prompt — fire immediately so the browser dialog appears without extra tap
          setPermission('prompt');
          void findHelpNow();
        }
      } catch {
        // can't query permissions API — just fire and let the browser ask
        if (mounted) setPermission('prompt');
        void findHelpNow();
      }
    };

    void checkPermission();
    return () => { mounted = false; };
  }, [findHelpNow]);

  const isBusy = status === 'locating' || status === 'searching';

  const statusLabel = useMemo(() => {
    if (status === 'locating') return 'Getting your location…';
    if (status === 'searching') return 'Finding nearest veterinary care…';
    if (status === 'done') return 'Nearest emergency vet found.';
    return '';
  }, [status]);

  return (
    <div className="w-full max-w-5xl mx-auto px-1 pb-4 space-y-4">
      <div className="px-1">
        <div className="flex items-start gap-2">
          <div>
            <div className="flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-error-red/60 mt-0.5 shrink-0" />
              <p className="text-xs text-error-red/60 leading-relaxed">
                KodaCare is <strong>not</strong> a triage or emergency dispatch service. Always call your vet or an emergency clinic directly. GPS location data may be inaccurate — always verify the address before travelling.
              </p>
            </div>
          </div>
        </div>

        {(status === 'idle' || status === 'error' || status === 'done') && (
          <button
            onClick={() => { void findHelpNow(); }}
            disabled={isBusy}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-error-red text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
          >
            <LocateFixed size={16} />
            {status === 'done' ? 'Search Again' : status === 'error' ? 'Retry' : 'Find Help Now'}
          </button>
        )}

        {isBusy && (
          <div className="mt-3 inline-flex items-center gap-2 text-sm text-error-red/75">
            <Loader2 size={16} className="animate-spin" />
            {status === 'locating' ? 'Getting your location…' : 'Searching nearby…'}
          </div>
        )}

        {permission === 'denied' && (
          <p className="text-xs text-error-red/75 mt-2">
            Location is blocked. Enable location permissions in browser settings and try again.
          </p>
        )}
        {permission === 'unsupported' && (
          <p className="text-xs text-error-red/75 mt-2">
            Your browser does not support geolocation.
          </p>
        )}
      </div>

      {errorText && (
        <div className="rounded-xl border border-error-red/20 bg-error-red/5 px-3 py-2 text-sm text-error-red">
          {errorText}
        </div>
      )}

      {nearest && (
        <>
          <div className="rounded-2xl border border-error-red/20 bg-error-red/5 p-4">
            <p className="text-xs uppercase tracking-wider font-bold text-error-red/60">Nearest result</p>
            <h4 className="text-lg font-bold text-error-red mt-1">{nearest.name}</h4>
            {nearest.address && (
              <p className="text-sm text-error-red/75 mt-1">{nearest.address}</p>
            )}
            {typeof nearest.distanceKm === 'number' && (
              <p className="text-sm text-error-red/75 mt-1">~ {nearest.distanceKm.toFixed(1)} km away</p>
            )}
            {nearest.phone && (
              <a
                href={`tel:${nearest.phone.replace(/\s+/g, '')}`}
                className="flex items-center justify-center gap-3 w-full mt-4 py-4 rounded-2xl bg-error-red text-white text-lg font-bold hover:opacity-90 active:opacity-80 transition-opacity"
              >
                <Phone size={22} />
                Call Now — {nearest.phone}
              </a>
            )}
            {nearest.mapsUrl && !nearest.lat && (
              <a
                href={nearest.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full mt-4 py-4 rounded-2xl bg-error-red text-white text-base font-bold hover:opacity-90 active:opacity-80 transition-opacity"
              >
                <MapPin size={20} />
                Open in Google Maps
              </a>
            )}
          </div>

          {nearest.lat && nearest.lng && userCoords && (
            <KodaMap
              vetName={nearest.name}
              vetLat={nearest.lat}
              vetLng={nearest.lng}
              userLat={userCoords.lat}
              userLng={userCoords.lng}
              mapsUrl={nearest.mapsUrl}
            />
          )}
        </>
      )}
    </div>
  );
}
